import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  TableSortLabel
} from '@mui/material';
import StatusBadge from '../common/StatusBadge';
import { formatDate } from '../../utils/formatters';

/**
 * BugsTable component for displaying bugs in a table
 * @param {Object} props - Component props
 * @param {Array} props.bugs - Array of bugs to display
 * @param {String} props.bugType - Type of bugs ('production' or 'internal')
 * @param {Object} props.prsByWorkItem - PRs grouped by work item ID
 * @param {Array} props.pullRequests - All pull requests
 * @param {Function} props.onPRCountClick - Callback when PR count is clicked
 * @returns {JSX.Element} BugsTable component
 */
const BugsTable = ({ 
  bugs = [], 
  bugType = 'production', 
  prsByWorkItem = {}, 
  pullRequests = [], 
  onPRCountClick = null,
  workItemTypeFilter = '',
  stateFilter = '',
  showOnlyWithPRs = false,
  showItemsWithoutParent = false
}) => {
  // State for sorting
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Get PR count for a bug
  const getPRCount = useMemo(() => {
    return (bugId) => {
      const bugIdStr = bugId.toString();
      const directPRs = prsByWorkItem[bugIdStr] || [];
      
      // Also search in all PRs for mentions of this bug ID
      const allPRs = pullRequests.filter(pr => {
        const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
        return prText.includes(`#${bugIdStr}`) || prText.includes(`ado-${bugIdStr}`) || prText.includes(`wi${bugIdStr}`);
      });
      
      // Combine and deduplicate
      const combined = [...directPRs, ...allPRs];
      const unique = combined.filter((pr, index, self) => 
        index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
      );
      
      return unique.length;
    };
  }, [prsByWorkItem, pullRequests]);
  
  // Apply filters to bugs
  const filteredBugs = useMemo(() => {
    let filtered = bugs.filter(bug => {
      const bugWorkItemType = bug.workItemType || 'Bug';
      const matchesWorkItemType = !workItemTypeFilter || bugWorkItemType === workItemTypeFilter;
      const matchesState = !stateFilter || bug.state === stateFilter;
      
      // Check if bug has PRs when showOnlyWithPRs is enabled
      let hasPRs = true;
      if (showOnlyWithPRs) {
        const bugIdStr = bug.id.toString();
        const directPRs = prsByWorkItem[bugIdStr] || [];
        const allPRs = pullRequests.filter(pr => {
          const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
          return prText.includes(`#${bugIdStr}`) || prText.includes(`ado-${bugIdStr}`) || prText.includes(`wi${bugIdStr}`);
        });
        const combined = [...directPRs, ...allPRs];
        const unique = combined.filter((pr, index, self) => 
          index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
        );
        hasPRs = unique.length > 0;
      }
      
      // When toggle is ON, show ONLY items without parent (excluding Epics)
      if (showItemsWithoutParent) {
        const hasNoParent = !bug.parent || bug.parent === '' || bug.parent === null;
        const isNotEpic = bug.workItemType !== 'Epic';
        const isItemWithoutParent = hasNoParent && isNotEpic;
        
        // Only show items without parent
        if (!isItemWithoutParent) {
          return false;
        }
      }
      // When toggle is OFF, show all items (no filtering by parent)
      
      return matchesWorkItemType && matchesState && hasPRs;
    });
    
    // Apply sorting
    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];
        
        // Handle special cases
        if (orderBy === 'id') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        } else if (orderBy === 'createdDate' || orderBy === 'closedDate') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        } else if (orderBy === 'priority') {
          // Priority sorting: P1 < P2 < P3 < P4
          const priorityOrder = { 'P1': 1, 'P2': 2, 'P3': 3, 'P4': 4 };
          aValue = priorityOrder[aValue] || 99;
          bValue = priorityOrder[bValue] || 99;
        } else if (orderBy === 'parent') {
          // Handle parent sorting - treat empty/null as empty string for sorting
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        } else {
          // String comparison
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [bugs, workItemTypeFilter, stateFilter, showOnlyWithPRs, showItemsWithoutParent, prsByWorkItem, pullRequests, orderBy, order]);

  return (
    <Box>
      {/* Table */}
      <TableContainer component={Paper} sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
      <Table aria-label={`${bugType} bugs table`} className="bugs-table">
        <TableHead>
          <TableRow>
            <TableCell width="80px">
              <TableSortLabel
                active={orderBy === 'id'}
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={() => handleRequestSort('id')}
              >
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'parent'}
                direction={orderBy === 'parent' ? order : 'asc'}
                onClick={() => handleRequestSort('parent')}
              >
                Parent
              </TableSortLabel>
            </TableCell>
            <TableCell width="25%">
              <TableSortLabel
                active={orderBy === 'title'}
                direction={orderBy === 'title' ? order : 'asc'}
                onClick={() => handleRequestSort('title')}
              >
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell width="90px">
              <TableSortLabel
                active={orderBy === 'state'}
                direction={orderBy === 'state' ? order : 'asc'}
                onClick={() => handleRequestSort('state')}
              >
                State
              </TableSortLabel>
            </TableCell>
            <TableCell width="90px">
              <TableSortLabel
                active={orderBy === 'priority'}
                direction={orderBy === 'priority' ? order : 'asc'}
                onClick={() => handleRequestSort('priority')}
              >
                Priority
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'createdDate'}
                direction={orderBy === 'createdDate' ? order : 'asc'}
                onClick={() => handleRequestSort('createdDate')}
              >
                Created Date
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'closedDate'}
                direction={orderBy === 'closedDate' ? order : 'asc'}
                onClick={() => handleRequestSort('closedDate')}
              >
                Closed Date
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'team'}
                direction={orderBy === 'team' ? order : 'asc'}
                onClick={() => handleRequestSort('team')}
              >
                Team
              </TableSortLabel>
            </TableCell>
            <TableCell width="120px">
              <TableSortLabel
                active={orderBy === 'issueSource'}
                direction={orderBy === 'issueSource' ? order : 'asc'}
                onClick={() => handleRequestSort('issueSource')}
              >
                Issue Source
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredBugs.map((bug) => (
            <TableRow key={bug.id}>
              <TableCell>
                <Link 
                  href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${bug.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ color: '#1976d2', textDecoration: 'none' }}
                >
                  {bug.id}
                </Link>
              </TableCell>
              <TableCell>
                {bug.parent ? (
                  <Link 
                    href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${bug.parent}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: '#1976d2', textDecoration: 'none' }}
                  >
                    {bug.parent}
                  </Link>
                ) : (
                  <Typography variant="body2" color="textSecondary">-</Typography>
                )}
              </TableCell>
              <TableCell>{bug.customer ? `[${bug.customer}] ${bug.title}` : bug.title}</TableCell>
              <TableCell>
                <StatusBadge status={bug.state} />
              </TableCell>
              <TableCell>
                <Chip 
                  label={bug.priority || 'P3'} 
                  size="small"
                  sx={{
                    backgroundColor: getPriorityColor(bug.priority),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </TableCell>
              <TableCell>{formatDate(bug.createdDate)}</TableCell>
              <TableCell>{bug.closedDate ? formatDate(bug.closedDate) : '-'}</TableCell>
              <TableCell>{bug.team || '-'}</TableCell>
              <TableCell>{bug.issueSource || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * Get color for a priority
 * @param {String} priority - Priority (P1, P2, P3, P4)
 * @returns {String} - Hex color code
 */
const getPriorityColor = (priority) => {
  switch (priority) {
    case 'P1':
      return '#FF0000'; // Red
    case 'P2':
      return '#FFA500'; // Orange
    case 'P3':
      return '#00B050'; // Green
    case 'P4':
      return '#0070C0'; // Blue
    default:
      return '#CCCCCC'; // Gray
  }
};

export default BugsTable;
